import { useUser } from "@clerk/remix";
import {
  Card,
  CardHeader,
  cn,
  Image,
  ScrollShadow,
  Spinner,
} from "@heroui/react";
import { Logger } from "@remix-pwa/sw";
import type { LoaderFunction, MetaFunction } from "@remix-run/cloudflare";
import {
  Link,
  useFetcher,
  useLoaderData,
  useNavigate,
  useSearchParams,
  useNavigation,
} from "@remix-run/react";
import { createClient } from "@supabase/supabase-js";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaFire,
  FaPlusCircle,
  FaSkull,
  FaUserAstronaut,
} from "react-icons/fa";

import { Database } from "~/types/supabase";
import { track } from "~/utils/analytics";
import { jsonLd, ogImageMeta } from "~/utils/seo";

const logger = new Logger({ prefix: "[/scenarios/explore]" });

type ExploreScenario = Pick<
  Database["public"]["Tables"]["scenarios"]["Row"],
  | "id"
  | "name"
  | "slug"
  | "image_url"
  | "image_position"
  | "difficulty"
  | "is_long_story"
  | "is_pro_only"
  | "play_count"
  | "creator_handle"
  | "owned_by"
  | "created_at"
>;

type LoaderData = {
  scenarios: ExploreScenario[];
  totalPublicScenarios: number;
  totalPlays: number;
  page: number;
  hasMore: boolean;
  sort: SortKey;
  difficulty: DifficultyKey;
  origin: string;
};

type SortKey = "trending" | "new";
type DifficultyKey = "all" | "easy" | "medium" | "hard";

const SORT_OPTIONS: { key: SortKey; label: string; icon: React.ReactNode }[] = [
  { key: "trending", label: "Trending", icon: <FaFire /> },
  { key: "new", label: "New", icon: <FaPlusCircle /> },
];

const DIFFICULTY_OPTIONS: { key: DifficultyKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "easy", label: "Easy" },
  { key: "medium", label: "Medium" },
  { key: "hard", label: "Hard" },
];

const PAGE_SIZE = 30;

const EXPLORE_TITLE = "Explore Scenarios — ManUp";
const EXPLORE_DESCRIPTION =
  "Browse hundreds of community-built dating scenarios. Practice the exact moments you freeze on — from first dates to shit tests to club cold approaches.";
const EXPLORE_OG_SOURCE =
  "https://cdn.manupgame.com/create-own-scenario-night-club-halloween.png";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const origin =
    (data as LoaderData | undefined)?.origin ?? "https://manupgame.com";
  const url = `${origin}/scenarios/explore`;
  const items = (data as LoaderData | undefined)?.scenarios ?? [];

  return [
    { title: EXPLORE_TITLE },
    { name: "description", content: EXPLORE_DESCRIPTION },
    { property: "og:type", content: "website" },
    { property: "og:title", content: EXPLORE_TITLE },
    { property: "og:description", content: EXPLORE_DESCRIPTION },
    { property: "og:url", content: url },
    ...ogImageMeta(EXPLORE_OG_SOURCE),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: EXPLORE_TITLE },
    { name: "twitter:description", content: EXPLORE_DESCRIPTION },
    { tagName: "link", rel: "canonical", href: url },
    jsonLd({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: EXPLORE_TITLE,
      description: EXPLORE_DESCRIPTION,
      url,
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: items.length,
        itemListElement: items.slice(0, 20).map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: s.slug ? `${origin}/s/${s.slug}` : `${origin}/scenarios/${s.id}`,
          name: s.name,
        })),
      },
    }),
  ];
};

export const loader: LoaderFunction = async (args) => {
  try {
    const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
    const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error("Missing Supabase env vars");
    }

    const url = new URL(args.request.url);
    const sort = (url.searchParams.get("sort") as SortKey) || "trending";
    const difficulty =
      (url.searchParams.get("difficulty") as DifficultyKey) || "all";
    const rawPage = Number(url.searchParams.get("page") ?? 0);
    const page = Number.isFinite(rawPage) && rawPage >= 0 ? Math.floor(rawPage) : 0;

    const supabase = createClient<Database>(supabaseUrl, supabaseKey);

    // We range one extra row so we can tell if there's a next page
    // without doing a separate COUNT(*) on every paginated load.
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE; // inclusive — yields PAGE_SIZE + 1 rows

    let query = supabase
      .from("scenarios")
      .select(
        "id, name, slug, image_url, image_position, difficulty, is_long_story, is_pro_only, play_count, creator_handle, owned_by, created_at",
        { count: "exact" }
      )
      .eq("is_public", true);

    if (difficulty !== "all") {
      query = query.eq("difficulty", difficulty);
    }

    if (sort === "new") {
      query = query.order("created_at", { ascending: false });
    } else {
      query = query
        .order("play_count", { ascending: false })
        .order("created_at", { ascending: false });
    }

    // Tie-breaker on id keeps pagination stable when many rows share the
    // same play_count or created_at value.
    query = query.order("id", { ascending: false }).range(from, to);

    const { data, error, count } = await query;
    if (error) {
      logger.error("Error loading public scenarios", error);
      throw new Error("Error loading public scenarios");
    }

    const rows = data ?? [];
    const hasMore = rows.length > PAGE_SIZE;
    const scenarios = hasMore ? rows.slice(0, PAGE_SIZE) : rows;

    const totalPlays = scenarios.reduce(
      (acc, s) => acc + (s.play_count ?? 0),
      0
    );

    return {
      scenarios,
      totalPublicScenarios: count ?? scenarios.length,
      totalPlays,
      page,
      hasMore,
      sort,
      difficulty,
      origin: url.origin,
    };
  } catch (error) {
    console.error(error);
    throw new Response("Internal Server Error", { status: 500 });
  }
};

export default function ExplorePage() {
  const loaderData = useLoaderData<LoaderData>();
  const {
    scenarios: initialScenarios,
    totalPublicScenarios,
    totalPlays: initialTotalPlays,
    sort,
    difficulty,
    page: initialPage,
    hasMore: initialHasMore,
  } = loaderData;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNavigating = navigation.state === "loading";

  // Local pagination state. The loader gives us page 0; "Load more" uses
  // a fetcher to grab subsequent pages and we append into local state.
  // Resetting whenever filters change (which re-runs the loader) is
  // handled by the useEffect below.
  const [scenarios, setScenarios] = useState(initialScenarios);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [accumulatedPlays, setAccumulatedPlays] = useState(initialTotalPlays);

  // Reset state when the loader returns new initial data (filters changed,
  // navigation back, etc).
  const filtersKey = `${sort}::${difficulty}`;
  const lastFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (lastFiltersKey.current !== filtersKey) {
      lastFiltersKey.current = filtersKey;
    }
    setScenarios(initialScenarios);
    setPage(initialPage);
    setHasMore(initialHasMore);
    setAccumulatedPlays(initialTotalPlays);
  }, [
    filtersKey,
    initialScenarios,
    initialPage,
    initialHasMore,
    initialTotalPlays,
  ]);

  const fetcher = useFetcher<LoaderData>();
  const isLoadingMore = fetcher.state !== "idle";

  // Append fetcher results when they come back.
  useEffect(() => {
    if (fetcher.state !== "idle" || !fetcher.data) return;
    const incoming = fetcher.data.scenarios;
    if (!incoming || incoming.length === 0) {
      setHasMore(false);
      return;
    }
    setScenarios((prev) => {
      // Dedupe in case the user double-clicks Load more.
      const seen = new Set(prev.map((s) => s.id));
      const fresh = incoming.filter((s) => !seen.has(s.id));
      return [...prev, ...fresh];
    });
    setPage(fetcher.data.page);
    setHasMore(fetcher.data.hasMore);
    setAccumulatedPlays((prev) => prev + fetcher.data!.totalPlays);
  }, [fetcher.state, fetcher.data]);

  useEffect(() => {
    track("explore_viewed", {
      sort,
      difficulty,
      result_count: scenarios.length,
    });
  }, [sort, difficulty, scenarios.length]);

  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;
    const next = new URLSearchParams(searchParams);
    next.set("page", String(page + 1));
    fetcher.load(`/scenarios/explore?${next.toString()}`);
  };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all" || (key === "sort" && value === "trending")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page"); // filter change always returns to first page
    track("explore_filter_changed", {
      filter_type: key === "sort" ? "sort" : "difficulty",
      value,
    });
    setSearchParams(next, { preventScrollReset: true });
  };

  const trendingThreshold = useMemo(() => {
    if (scenarios.length === 0) return Infinity;
    const counts = scenarios
      .map((s) => s.play_count ?? 0)
      .sort((a, b) => b - a);
    return counts[Math.min(2, counts.length - 1)] ?? Infinity;
  }, [scenarios]);

  return (
    <div className="flex flex-col h-dvh w-full">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 sticky top-0 z-30 bg-neutral-950/85 backdrop-blur-md border-b-1 border-neutral-800/60">
        <div className="flex flex-row items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="p-2 -ml-2 active:opacity-60"
          >
            <FaArrowLeft className="text-lg" />
          </button>
          <h1 className="font-bangers text-xl">Explore</h1>
          <Link
            to="/scenarios/create"
            aria-label="Create scenario"
            onClick={() => track("explore_create_clicked", { from: "header" })}
            className="p-2 -mr-2 active:opacity-60"
          >
            <FaPlusCircle className="text-lg text-primary" />
          </Link>
        </div>

        <div className="mt-3 flex flex-row items-baseline gap-3 font-bangers">
          <span className="text-sm opacity-70">
            {totalPublicScenarios.toLocaleString()} scenarios
          </span>
          <span className="text-sm opacity-50">·</span>
          <span className="text-sm opacity-70">
            {accumulatedPlays.toLocaleString()}
            {hasMore ? "+" : ""} plays loaded
          </span>
        </div>

        {/* Filter chips */}
        <ScrollShadow
          orientation="horizontal"
          hideScrollBar
          className="mt-3 -mx-5 px-5"
        >
          <div className="flex flex-row gap-2 pb-1">
            {SORT_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.key}
                isActive={sort === opt.key}
                onClick={() => setParam("sort", opt.key)}
              >
                <span className="inline-flex items-center gap-1.5">
                  {opt.icon}
                  {opt.label}
                </span>
              </FilterChip>
            ))}
            <span className="w-px bg-neutral-800 mx-1" aria-hidden />
            {DIFFICULTY_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.key}
                isActive={difficulty === opt.key}
                onClick={() => setParam("difficulty", opt.key)}
              >
                {opt.label}
              </FilterChip>
            ))}
          </div>
        </ScrollShadow>
      </div>

      {/* Grid */}
      <ScrollShadow className="flex-1 px-5 pt-5 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
        {isNavigating && scenarios.length === 0 ? (
          <div className="w-full flex justify-center items-center pt-20">
            <Spinner color="white" variant="wave" />
          </div>
        ) : scenarios.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              {scenarios.map((scenario, index) => (
                <ExploreCard
                  key={scenario.id}
                  scenario={scenario}
                  position={index}
                  isTrending={
                    sort === "trending" &&
                    (scenario.play_count ?? 0) >= trendingThreshold &&
                    (scenario.play_count ?? 0) > 0
                  }
                />
              ))}
            </motion.div>

            {hasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-5 py-2 rounded-full font-bangers text-sm bg-neutral-900 border-1 border-neutral-700 active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center gap-2 min-w-[140px] justify-center"
                  aria-label="Load more scenarios"
                >
                  {isLoadingMore ? (
                    <Spinner
                      color="white"
                      variant="wave"
                      classNames={{ base: "w-4 h-4" }}
                    />
                  ) : (
                    <>Load more</>
                  )}
                </button>
              </div>
            )}

            {!hasMore && scenarios.length > PAGE_SIZE && (
              <p className="mt-6 text-center text-xs font-mono opacity-50">
                You&rsquo;ve reached the end. Build a new one?
              </p>
            )}
          </>
        )}

        <CreatePromptFooter />
      </ScrollShadow>
    </div>
  );
}

const FilterChip = ({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "shrink-0 font-bangers text-sm px-3 py-1.5 rounded-full border-1 transition-colors active:scale-95",
      isActive
        ? "bg-primary border-primary text-white"
        : "bg-neutral-900 border-neutral-700 text-neutral-300 hover:border-neutral-500"
    )}
  >
    {children}
  </button>
);

const ExploreCard = ({
  scenario,
  isTrending,
  position,
}: {
  scenario: ExploreScenario;
  isTrending: boolean;
  position: number;
}) => {
  const { user } = useUser();
  const isMine = scenario.owned_by === user?.id;
  const href = scenario.slug
    ? `/s/${scenario.slug}`
    : `/scenarios/${scenario.id}`;

  return (
    <Link
      to={href}
      prefetch="intent"
      onClick={() => {
        track("explore_scenario_clicked", {
          scenario_id: scenario.id,
          slug: scenario.slug,
          position,
        });
      }}
      className="block active:opacity-90"
    >
      <div className="relative">
        {isTrending && (
          <div className="absolute -top-2 left-3 z-20 px-2 py-0.5 bg-primary text-white rounded-md text-xs font-bangers flex items-center gap-1 border-1 border-black shadow-md">
            <FaFire /> Trending
          </div>
        )}
        <div className="absolute -top-2 right-3 z-20 px-2 py-0.5 bg-white text-black rounded-md text-xs font-bangers flex items-center gap-1 border-1 border-black">
          <FaSkull /> {scenario.difficulty}
        </div>

        <Card fullWidth className="rounded-3xl overflow-hidden">
          <CardHeader className="absolute z-10 flex-col h-full items-start justify-end p-4 from-transparent via-black/20 to-black/80 bg-gradient-to-b">
            <h3 className="text-white font-bangers text-2xl leading-tight drop-shadow-[2px_2px_0.1px_rgba(0,0,0,1)]">
              {scenario.name}
            </h3>
            <div className="mt-1 flex flex-row items-center justify-between w-full text-xs font-mono text-white/85">
              <span className="inline-flex items-center gap-1.5 truncate max-w-[60%]">
                <FaUserAstronaut className="opacity-80 shrink-0" />
                <span className="truncate">
                  {isMine
                    ? "you"
                    : scenario.creator_handle
                    ? `@${scenario.creator_handle}`
                    : scenario.owned_by
                    ? "@anonymous"
                    : "ManUp"}
                </span>
              </span>
              <span className="inline-flex items-center gap-1 opacity-90 font-bangers tracking-wide">
                {formatPlayCount(scenario.play_count ?? 0)} plays
              </span>
            </div>
          </CardHeader>

          <Image
            src={scenario.image_url}
            alt={`Cover image for the ${scenario.difficulty} dating scenario "${scenario.name}"`}
            height={140}
            width={9999}
            className={cn(
              "object-cover w-full relative z-0",
              scenario.image_position
            )}
          />
        </Card>
      </div>
    </Link>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center text-center font-bangers py-20 gap-2 opacity-80">
    <h2 className="text-2xl">Nothing here yet</h2>
    <p className="text-sm font-mono opacity-70 max-w-xs">
      No scenarios match these filters. Try a different difficulty — or build
      one yourself.
    </p>
    <Link
      to="/scenarios/create"
      onClick={() => track("explore_create_clicked", { from: "empty_state" })}
      className="mt-4 inline-flex items-center gap-2 text-primary"
    >
      <FaPlusCircle /> Create one
    </Link>
  </div>
);

const CreatePromptFooter = () => (
  <div className="mt-10 mb-4 rounded-2xl border-1 border-dashed border-neutral-700 px-5 py-6 text-center font-bangers">
    <p className="text-lg leading-tight">Want yours featured?</p>
    <p className="text-xs font-mono opacity-60 mt-1 leading-snug">
      Build a scenario from a real moment in your life. The more people play
      it, the higher it climbs.
    </p>
    <Link
      to="/scenarios/create"
      onClick={() => track("explore_create_clicked", { from: "footer" })}
      className="mt-4 inline-flex items-center gap-2 text-primary text-base"
    >
      <FaPlusCircle /> Create scenario
    </Link>
  </div>
);

const formatPlayCount = (n: number): string => {
  if (n < 1000) return n.toString();
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}m`;
};
